// ABOUTME: One half of a divided section in the builder — a flex-weighted, draggable column of rows.
// ABOUTME: Drag the grip to swap halves; drag either border (column-style edge handles) to re-weight its flex.
import { useState } from "react";
import { Box, ButtonBase, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../theme";
import { useDroppable } from "@dnd-kit/core";
import { useSortable, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RhDragBar } from "./RhDragBar";
import { ReportBuilderRow } from "./ReportBuilderRow";

// The page-level sections container key; any other containerKey is a tab id (i.e. inside the tab body).
const PAGE_SECTIONS = "sections";

export const ReportBuilderDividedSection = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    // Halves inside the tab body sit on a near-white tab background, so they get a distinct lighter card in
    // light mode to keep them readable (dark mode is unchanged).
    const inTabBody = props.containerKey !== PAGE_SECTIONS;

    const [hoverEdge, setHoverEdge] = useState(null);
    const [resizingEdge, setResizingEdge] = useState(null);

    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: props.divSec.id,
        data: { type: "dividedSection", containerKey: props.containerKey, sectionIndex: props.si },
    });

    // Drop zone so a row can be dropped back into an EMPTY half (no sibling row to land next to). Merged onto
    // the half's node; only active while the half has no rows.
    const { setNodeRef: setRowZoneRef } = useDroppable({
        id: `rowzone-${props.divSec.id}`,
        data: { type: "rowContainer", containerKey: props.containerKey, sectionIndex: props.si, dividerIndex: props.di },
        disabled: props.divSec.children.length > 0,
    });
    const setRefs = (node) => { setNodeRef(node); setRowZoneRef(node); };

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
    };

    // Drag-to-resize from either border (mirrors ReportBuilderColumn). dir flips per edge so each border
    // resizes outward; the half's flex weight changes in whole steps (clamped in reportBuilderResizeDividedSection).
    const startResize = (e, dir) => {
        setResizingEdge(dir < 0 ? "left" : "right");
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = props.divSec.width ?? 1;

        const onMouseMove = (moveEvent) => {
            const delta = (moveEvent.clientX - startX) * dir;
            const newWidth = Math.max(1, startWidth + Math.round(delta / 80));
            props.handleResizeDividedSection(props.containerKey, props.si, props.di, newWidth);
        };
        const onMouseUp = () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            setResizingEdge(null);
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
    };

    // Draggable hit zone pinned to a border, kept within the half's 12px padding gutter so it never overlaps
    // the cells' own edge-resize zones inside the content.
    const edgeHitSx = {
        position: "absolute",
        top: 0,
        bottom: 0,
        width: 12,
        cursor: "ew-resize",
        zIndex: 4,
    };
    // Full rounded border clipped to one edge strip, so the stroke curves around that side's corners.
    const edgeSurfaceSx = {
        position: "absolute",
        inset: 0,
        borderRadius: 4,
        border: `8px solid ${theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[300]}`,
        pointerEvents: "none",
        transition: "opacity 120ms ease",
        zIndex: 3,
    };

    return (
        <Box
            ref={setRefs}
            style={style}
            className="report-divider-section"
            sx={{
                flex: props.divSec.width ?? 1,
                minWidth: 0,
                minHeight: props.divSec.children.length === 0 ? 120 : undefined,
                position: "relative",
                zIndex: 30, // sit above the parent section's drag bar so this half stays grabbable (section grabs via the gap)
                flexDirection:'column',
                display:"flex",
                gap:2,
                p:1.5,
                pb:3,
                background:theme.palette.mode === "dark" ? colors.grey[300] : (inTabBody ? colors.grey[100] : colors.grey[200]),
                borderRadius:4,
            }}
        >
            {/* Top accent bar = grab to move/swap this half. Inset from the side resize edges. */}
            <RhDragBar
                activatorRef={setActivatorNodeRef}
                attributes={attributes}
                listeners={listeners}
                left={0}
                right={0}
                zIndex={4}
                accent={colors.green[400]}
            />

            <SortableContext
                items={props.divSec.children.map(r => r.id)}
                strategy={verticalListSortingStrategy}
            >
                {props.divSec.children.map((row, ri) => (
                    <ReportBuilderRow
                        key={row.id}
                        row={row}
                        si={props.si}
                        di={props.di}
                        ri={ri}
                        containerKey={props.containerKey}
                        rowPath={`${props.divSecPath}.children.${ri}`}

                        handleRemoveRow={props.handleRemoveRow}
                        handleAddColumn={props.handleAddColumn}
                        handleRemoveColumn={props.handleRemoveColumn}
                        handleResizeColumn={props.handleResizeColumn}

                        onOpenReportPicker={props.onOpenReportPicker}
                        handleSetColumnChart={props.handleSetColumnChart}
                    />
                ))}
            </SortableContext>

            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                }}
            >
                <ButtonBase
                    className="remove-divided-section-btn"
                    onClick={() => props.handleRemoveDividedSection(props.containerKey, props.si, props.di)}
                    sx={{
                        width: 1,
                        height: 32,
                        borderRadius: 4,
                        background: colors.orange[100],
                        color: colors.orange[400],
                        "&:hover": {
                            background: colors.orange[200]
                        }
                    }}
                >
                    {t("remove_divided_section")}
                </ButtonBase>

                <ButtonBase
                    className="add-row-btn"
                    onClick={() => props.handleAddRow(props.containerKey, props.si, props.di)}
                    sx={{
                        width: 1,
                        height: 32,
                        borderRadius: 4,
                        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                        "&:hover": {
                            background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200],
                        }
                    }}
                >
                    {t("add_row")}
                </ButtonBase>
            </Box>

            {/* Curved border surfaces — shown while their edge is hovered or actively resizing. */}
            <Box sx={{ ...edgeSurfaceSx, clipPath: "inset(0 calc(100% - 10px) 0 0)", opacity: (hoverEdge === "left" || resizingEdge === "left") ? 1 : 0 }} />
            <Box sx={{ ...edgeSurfaceSx, clipPath: "inset(0 0 0 calc(100% - 10px))", opacity: (hoverEdge === "right" || resizingEdge === "right") ? 1 : 0 }} />

            {/* Drag hit zones on both borders — drag to grow or shrink this half. */}
            <Box
                sx={{ ...edgeHitSx, left: 0 }}
                onMouseEnter={() => setHoverEdge("left")}
                onMouseLeave={() => setHoverEdge(null)}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => startResize(e, -1)}
            />
            <Box
                sx={{ ...edgeHitSx, right: 0 }}
                onMouseEnter={() => setHoverEdge("right")}
                onMouseLeave={() => setHoverEdge(null)}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => startResize(e, 1)}
            />
        </Box>
    );
};
